import { Button, BUTTON_STATUS, COLORS, RadioButton, SIZE } from '@dehaat/dds';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { ReactElement, useCallback, useContext, useState } from 'react';
import { CLIENT_NAME_COOKIE } from '@/constants/common';
import { PAYMENT_OPTIONS, paymentDrawerData, payNowDrawerOption } from '@/constants/payments';
import { UserContext } from '@/context/UserProvider';
import { getCookieValue, isFromSpiceMoney3P } from '@/utils/helper';
import CreditLabel from '../credit/CreditLabel';
import RoundedBottomDrawer from '../RoundedBottomDrawer';
interface Props {
  maximumAmount?: number;
  minimumAmount?: number;
  creditLimit?: number;
  show: boolean;
  onClose: VoidFunction;
  onConfirmAndProceed: (option: PAYMENT_OPTIONS) => void;
  setCreditPayment?: (value: boolean) => void;
  showCreditLimit?: boolean;
  // Present only when the buyer holds an active Kisan Card membership.
  kisanCard?: {
    balanceRupees: number;
    orderTotalRupees: number;
  };
}
const PaymentDrawer = ({
  show,
  onConfirmAndProceed,
  onClose,
  creditLimit = 0,
  maximumAmount = 0,
  minimumAmount = 0,
  setCreditPayment,
  showCreditLimit = false,
  kisanCard
}: Props) => {
  const {
    t
  } = useTranslation('payments');
  const [selectedMethod, setSelectedMethod] = useState<{
    paymentId: PAYMENT_OPTIONS;
    index: number;
  }>();
  const {
    isCreditUser
  } = useContext(UserContext);
  const getRadioItems = useCallback(() => {
    const radioElements: Array<{
      radioElement: ReactElement;
      value: PAYMENT_OPTIONS;
    }> = [];
    if (isFromSpiceMoney3P(getCookieValue(CLIENT_NAME_COOKIE))) {
      const item = {
        radioElement: <div className="flex items-center">
            <div className="flex-1">
              <p className="text-neutral-90 text-sm">{'Spice Money Wallet'}</p>
              <p className="text-netural-80 mt-1 text-sm">{''}</p>
            </div>
          </div>,
        value: PAYMENT_OPTIONS.SPICE_MONEY_3P
      };
      radioElements.push(item);
      return radioElements;
    }
    // A Kisan Card holder is sent here instead of the prepay "select amount"
    // drawer, so on prepay orders (no COD) the gateway has to be offered here
    // too; otherwise the card would be the only way to pay.
    const drawerOptions = kisanCard && minimumAmount > 0 ? [payNowDrawerOption, ...paymentDrawerData] : paymentDrawerData;
    drawerOptions.filter(({
      type
    }) => !(type == PAYMENT_OPTIONS.COD && minimumAmount > 0)).map(({
      title,
      desc,
      image,
      type
    }) => {
      const item = {
        radioElement: <div className="flex items-center">
              <div className="flex-1">
                <p className="text-neutral-90 text-sm">{t(title)}</p>
                <p className="text-netural-80 mt-1 text-sm">{t(desc)}</p>
              </div>

              <Image src={image} alt={t(title)} width={32} height={32} />
            </div>,
        value: type,
        isDisabled: type == PAYMENT_OPTIONS.COD && minimumAmount > 0
      };
      radioElements.push(item);
    });
    if (isCreditUser && showCreditLimit) {
      const item = {
        radioElement: <CreditLabel availableCreditLimit={creditLimit} maximumAmount={maximumAmount} />,
        value: PAYMENT_OPTIONS.CREDIT,
        isDisabled: maximumAmount >= creditLimit
      };
      radioElements.push(item);
    }
    if (kisanCard) {
      const balanceRupees = kisanCard.balanceRupees;
      const insufficient = kisanCard.balanceRupees < kisanCard.orderTotalRupees;
      const item = {
        radioElement: <div className="flex items-center">
            <div className="flex-1">
              <p className="text-neutral-90 text-sm">Kisan Card</p>
              <p className="text-netural-80 mt-1 text-sm">
                {`Balance ₹${balanceRupees.toLocaleString('en-IN')}${insufficient ? ' · Insufficient balance' : ''}`}
              </p>
            </div>
          </div>,
        value: PAYMENT_OPTIONS.KISAN_CARD,
        isDisabled: insufficient
      };
      radioElements.push(item);
    }
    return radioElements;
  }, [creditLimit, isCreditUser, maximumAmount, minimumAmount, showCreditLimit, kisanCard, t]);
  return <>
      {<RoundedBottomDrawer show={show} onClose={onClose}>
          <h5 className="mb-[22px]">{t('choose_payment_options')}</h5>
          <RadioButton radioOuterContainerStyle={{
        borderColor: COLORS.neutral10,
        borderRadius: 12,
        borderWidth: 1
      }} containerStyle={{
        gap: 16,
        paddingHorizontal: 20,
        paddingVertical: 16
      }} list={getRadioItems()} onClick={idx => {
        const selecteValue = getRadioItems()[idx].value;
        setSelectedMethod({
          paymentId: selecteValue,
          index: idx
        });
        if (selecteValue === PAYMENT_OPTIONS.CREDIT) {
          setCreditPayment?.(true);
        } else {
          setCreditPayment?.(false);
        }
      }} selected={selectedMethod?.index} />
          <Button status={selectedMethod !== undefined ? BUTTON_STATUS.ACTIVE : BUTTON_STATUS.DISABLED} onClick={() => {
        if (selectedMethod === undefined) {
          return;
        }
        onConfirmAndProceed(selectedMethod.paymentId as PAYMENT_OPTIONS);
      }} text={t('confirm_and_proceed')} size={SIZE.large} buttonStyle={{
        marginTop: 34
      }} />
        </RoundedBottomDrawer>}
    </>;
};
export default PaymentDrawer;